import moment from "moment";
import "moment/locale/pt-br";
import intl from "react-intl-universal";
import { formatCurrency } from "../../../Utils/formatters";
import {
  getExtractTransactionAmountCents,
  isExtractCreditTransaction,
} from "./extractFilters";

moment.locale("pt-br");

const formatDayHeader = (dateStr) =>
  moment(dateStr).format("DD MMMM YYYY").toUpperCase();

const formatMonthHeader = (dateStr) =>
  moment(dateStr).format("MMMM YYYY").toUpperCase();

const summarizeDay = (items = []) => {
  let credits = 0;
  let debits = 0;

  items.forEach((item) => {
    const amount = getExtractTransactionAmountCents(item);
    if (amount === 0) return;

    if (isExtractCreditTransaction(item)) credits += amount;
    else debits += amount;
  });

  return { credits, debits, count: items.length };
};

const getDayClosingBalance = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return 0;
  const sorted = [...items].sort((a, b) => {
    const ta = moment(a?.created_at).valueOf();
    const tb = moment(b?.created_at).valueOf();
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
  });
  return sorted[sorted.length - 1]?.balance_after ?? 0;
};

const summarizeMonth = (dayGroups = []) => {
  const months = new Map();

  dayGroups.forEach((group) => {
    if (!group) return;
    const day = Object.keys(group)[0];
    const items = group[day] ?? [];
    if (items.length === 0) return;

    const monthKey = moment(day).format("YYYY-MM");
    const monthLabel = formatMonthHeader(day);

    if (!months.has(monthKey)) {
      months.set(monthKey, {
        key: monthKey,
        label: monthLabel,
        credits: 0,
        debits: 0,
        count: 0,
        days: 0,
        latestDay: null,
        closingBalance: 0,
      });
    }

    const summary = summarizeDay(items);
    const entry = months.get(monthKey);
    entry.credits += summary.credits;
    entry.debits += summary.debits;
    entry.count += summary.count;
    entry.days += 1;

    if (!entry.latestDay || day > entry.latestDay) {
      entry.latestDay = day;
      entry.closingBalance = getDayClosingBalance(items);
    }
  });

  return [...months.values()].sort((a, b) => b.key.localeCompare(a.key));
};

export const buildConsolidatedDailyRows = (dayGroups = []) =>
  dayGroups
    .map((group, index) => {
      if (!group) return null;
      const day = Object.keys(group)[0];
      const items = group[day] ?? [];
      if (items.length === 0) return null;

      const { credits, debits, count } = summarizeDay(items);

      return {
        key: `${day}-${index}`,
        heading: formatDayHeader(day),
        meta: intl.get("extract.entriesCount", { count }),
        credits,
        debits,
        balance: getDayClosingBalance(items),
      };
    })
    .filter(Boolean);

export const buildConsolidatedMonthRows = (dayGroups = []) =>
  summarizeMonth(dayGroups).map((month) => ({
    key: month.key,
    heading: month.label,
    meta: intl.get("extract.monthSummaryMeta", {
      days: month.days,
      count: month.count,
    }),
    credits: month.credits,
    debits: month.debits,
    balance: month.closingBalance,
  }));

const ConsolidatedAmount = ({ label, value, tone }) => (
  <div className={`extract-consolidated__metric extract-consolidated__metric--${tone}`}>
    <span className="extract-consolidated__metric-label">{label}</span>
    <span className="extract-consolidated__metric-value">{value}</span>
  </div>
);

const ConsolidatedAmounts = ({ credits, debits, balance }) => (
  <div className="extract-consolidated__amounts">
    <ConsolidatedAmount
      tone="credit"
      label={intl.get("extract.creditsLabel")}
      value={`+${formatCurrency(credits)}`}
    />
    <ConsolidatedAmount
      tone="debit"
      label={intl.get("extract.debitsLabel")}
      value={`-${formatCurrency(debits)}`}
    />
    <ConsolidatedAmount
      tone="balance"
      label={intl.get("extract.balanceLabel")}
      value={formatCurrency(Math.abs(balance ?? 0))}
    />
  </div>
);

const ConsolidatedRow = ({ heading, meta, credits, debits, balance }) => (
  <div className="extract-consolidated__row">
    <div className="extract-consolidated__info min-w-0">
      <p className="extract-consolidated__heading m-0">{heading}</p>
      <p className="extract-consolidated__meta m-0">{meta}</p>
    </div>
    <ConsolidatedAmounts credits={credits} debits={debits} balance={balance} />
  </div>
);

const ConsolidatedList = ({ rows = [] }) => {
  if (rows.length === 0) {
    return (
      <div className="app-empty-state m-4">{intl.get("common.noRecordsFiltered")}</div>
    );
  }

  return (
    <div className="extract-consolidated">
      {rows.map((row) => (
        <ConsolidatedRow key={row.key} {...row} />
      ))}
    </div>
  );
};

export const ExtractDailyConsolidated = ({ dayGroups = [] }) => (
  <ConsolidatedList rows={buildConsolidatedDailyRows(dayGroups)} />
);

export const ExtractMonthlyConsolidated = ({ rows = [] }) => (
  <ConsolidatedList rows={rows} />
);

export const ExtractFutureEntries = () => (
  <div className="app-empty-state m-4">
    <p className="m-0">{intl.get("extract.noFutureEntriesTitle")}</p>
    <p className="m-0 mt-1">{intl.get("extract.noFutureEntriesDescription")}</p>
  </div>
);
